// CODA-1008: Neukundenonboarding – 4-Schritte-Wizard.
// CODA-1944: Letzter Schritt ist "Phase-1-Link senden" (keine Dokumente im Wizard).
import { useState, useEffect, useRef } from 'react'
import { isAxiosError } from 'axios'
import apiClient from '@/lib/api'
import { useTenantStore } from '@/stores/tenants'
import {
  Modal,
  Button,
  Input,
  Textarea,
  SelectRoot,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Spinner,
  ErrorState,
  useToast,
} from '@/components/ui'
import {
  CheckCircle,
  RefreshCw,
  Send,
  Copy,
} from 'lucide-react'

// ─── Types ───────────────────────────────────────────────────────────────────

interface TokenResult {
  token: string
  expiresAt: string
  portalUrl: string
  emailSent: boolean
}

interface WizardProps {
  onClose: () => void
  onComplete: () => void
}

// ─── Step indicator ───────────────────────────────────────────────────────────

const STEPS = [
  'Stammdaten',
  'Ansprechpartner',
  'SAP Business Partner',
  'Phase-1-Link senden',
]

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center gap-0 mb-6">
      {STEPS.map((label, i) => {
        const idx = i + 1
        const done = idx < current
        const active = idx === current
        return (
          <div key={idx} className="flex items-center flex-1 min-w-0">
            <div className="flex flex-col items-center shrink-0">
              <div
                className={[
                  'w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2',
                  done
                    ? 'bg-brand-500 border-brand-500 text-white'
                    : active
                      ? 'bg-white border-brand-500 text-brand-600'
                      : 'bg-white border-gray-300 text-gray-400',
                ].join(' ')}
              >
                {done ? <CheckCircle className="h-4 w-4" /> : idx}
              </div>
              <span
                className={[
                  'text-xs mt-1 text-center leading-tight max-w-[72px]',
                  active ? 'text-brand-600 font-medium' : done ? 'text-brand-500' : 'text-gray-400',
                ].join(' ')}
              >
                {label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                className={[
                  'flex-1 h-0.5 mx-1 -mt-5',
                  done ? 'bg-brand-500' : 'bg-gray-200',
                ].join(' ')}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

// ─── Wizard ───────────────────────────────────────────────────────────────────

export function OnboardingWizardModal({ onClose, onComplete }: WizardProps) {
  const { toast } = useToast()
  const [step, setStep] = useState(1)
  const tenants = useTenantStore((s) => s.tenants)
  const activeTenantId = useTenantStore((s) => s.activeTenantId)

  // Step 1 – Stammdaten
  const [tenantId, setTenantId] = useState(() => activeTenantId ?? '')
  const [companyName, setCompanyName] = useState('')
  const [street, setStreet] = useState('')
  const [zip, setZip] = useState('')
  const [city, setCity] = useState('')
  const [country, setCountry] = useState('DE')
  const [notes, setNotes] = useState('')

  // Step 2 – Ansprechpartner
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')

  // Step 3 – SAP BP
  const [sapStatus, setSapStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [sapError, setSapError] = useState<string | null>(null)
  const [requestId, setRequestId] = useState<string | null>(null)
  const [sapCardCode, setSapCardCode] = useState<string | null>(null)
  const sapTriggered = useRef(false)

  // Step 4 – Phase-1-Link
  const [ttlHours, setTtlHours] = useState(720)
  const [tokenResult, setTokenResult] = useState<TokenResult | null>(null)
  const [tokenLoading, setTokenLoading] = useState(false)
  const [tokenError, setTokenError] = useState<string | null>(null)

  // ─── Step 1 validation ─────────────────────────────────────────────────────

  const step1Valid = tenantId.length > 0 && companyName.trim().length > 0
  const step2Valid = email.trim().length > 0 && email.includes('@')

  // ─── Step 3: trigger SAP BP creation ──────────────────────────────────────

  async function triggerSapBp() {
    setSapStatus('loading')
    setSapError(null)
    sapTriggered.current = true
    try {
      const { data } = await apiClient.post('/onboarding', {
        companyName: companyName.trim(),
        companyStreet: street.trim() || null,
        companyZip: zip.trim() || null,
        companyCity: city.trim() || null,
        companyCountry: country,
        contactFirstName: firstName.trim() || null,
        contactLastName: lastName.trim() || null,
        contactEmail: email.trim(),
        contactPhone: phone.trim() || null,
        notes: notes.trim() || null,
      }, { headers: { 'X-Tenant-Id': tenantId } })
      setRequestId(data.id)
      setSapCardCode(data.sap_card_code ?? null)
      setSapStatus('success')
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 422) {
        const body = err.response.data as { sapError?: string; requestId?: string }
        setSapError(body.sapError ?? 'SAP Business Partner konnte nicht angelegt werden')
        if (body.requestId) setRequestId(body.requestId)
      } else {
        const msg = isAxiosError(err)
          ? (err.response?.data?.error ?? err.message)
          : 'Unbekannter Fehler'
        setSapError(msg)
      }
      setSapStatus('error')
    }
  }

  useEffect(() => {
    if (step === 3 && !sapTriggered.current) {
      triggerSapBp()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])

  // ─── Step 4: Phase-1-Token generieren und senden ───────────────────────────

  async function sendPhase1Token() {
    if (!requestId) return
    setTokenLoading(true)
    setTokenError(null)
    try {
      const { data } = await apiClient.post(`/onboarding/${requestId}/token`, {
        tokenTtlHours: ttlHours,
        phase: 'phase1',
      })
      setTokenResult(data)
    } catch (err) {
      const msg = isAxiosError(err)
        ? (err.response?.data?.error ?? err.message)
        : 'Unbekannter Fehler'
      setTokenError(msg)
    } finally {
      setTokenLoading(false)
    }
  }

  // ─── Navigation helpers ────────────────────────────────────────────────────

  function next() { setStep((s) => Math.min(s + 1, 4)) }
  function back() { setStep((s) => Math.max(s - 1, 1)) }

  // ─── Render ────────────────────────────────────────────────────────────────

  const title = `Neues Onboarding – Schritt ${step} von ${STEPS.length}: ${STEPS[step - 1]}`

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      size="lg"
    >
      <StepIndicator current={step} />

      {/* ── Schritt 1: Stammdaten ────────────────────────────────────────────── */}
      {step === 1 && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mandant *</label>
            <SelectRoot value={tenantId} onValueChange={setTenantId}>
              <SelectTrigger>
                <SelectValue placeholder="Mandant auswählen…" />
              </SelectTrigger>
              <SelectContent>
                {tenants.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </SelectRoot>
          </div>
          <Input
            label="Firmenname *"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="Mustermann GmbH"
          />
          <Input
            label="Straße"
            value={street}
            onChange={(e) => setStreet(e.target.value)}
            placeholder="Musterstraße 1"
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="PLZ"
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              placeholder="12345"
            />
            <Input
              label="Stadt"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Musterstadt"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Land</label>
            <SelectRoot value={country} onValueChange={setCountry}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="DE">Deutschland</SelectItem>
                <SelectItem value="AT">Österreich</SelectItem>
                <SelectItem value="CH">Schweiz</SelectItem>
              </SelectContent>
            </SelectRoot>
          </div>
          <Textarea
            label="Interne Notiz"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optionale interne Notiz…"
            rows={3}
          />
        </div>
      )}

      {/* ── Schritt 2: Ansprechpartner ───────────────────────────────────────── */}
      {step === 2 && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Vorname"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Max"
            />
            <Input
              label="Nachname"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Mustermann"
            />
          </div>
          <Input
            label="E-Mail *"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="max.mustermann@firma.de"
          />
          <Input
            label="Telefon"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+49 123 456789"
          />
        </div>
      )}

      {/* ── Schritt 3: SAP Business Partner ─────────────────────────────────── */}
      {step === 3 && (
        <div className="flex flex-col items-center justify-center min-h-[200px] gap-4">
          {sapStatus === 'loading' && (
            <div className="flex flex-col items-center gap-3">
              <Spinner size="lg" />
              <p className="text-sm text-gray-500">SAP Business Partner wird angelegt…</p>
            </div>
          )}

          {sapStatus === 'success' && (
            <div className="flex flex-col items-center gap-3 text-center">
              <div className="rounded-full bg-green-100 p-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <p className="font-medium text-gray-800">Business Partner erfolgreich angelegt</p>
              {sapCardCode && (
                <p className="text-sm text-gray-500">SAP CardCode: <span className="font-mono font-semibold text-gray-700">{sapCardCode}</span></p>
              )}
            </div>
          )}

          {sapStatus === 'error' && (
            <div className="w-full">
              <ErrorState
                title="SAP Business Partner konnte nicht angelegt werden"
                description="Der Onboarding-Vorgang wurde trotzdem gespeichert. Prüfe die technischen Details oder versuche es erneut."
                details={sapError ?? 'Unbekannter Fehler'}
              />
              <div className="flex justify-center mt-4">
                <Button
                  variant="secondary"
                  onClick={() => {
                    sapTriggered.current = false
                    triggerSapBp()
                  }}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Erneut versuchen
                </Button>
              </div>
              {requestId && (
                <p className="text-xs text-gray-400 text-center mt-2">
                  Der Onboarding-Vorgang wurde erstellt (ID: {requestId}). SAP-BP kann später über Retry angelegt werden.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Schritt 4: Phase-1-Link senden ───────────────────────────────────── */}
      {step === 4 && (
        <div className="space-y-5">
          <div className="rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm text-blue-800">
            <p className="font-medium mb-1">Phase 1 – Stammdaten-Bestätigung</p>
            <p className="text-blue-700">
              Der Kunde erhält einen Link, über den er seine Unternehmensstammdaten und Ansprechpartner prüfen und bestätigen kann.
              Vertragsdokumente werden erst in Phase 2 versendet.
            </p>
          </div>

          <p className="text-sm text-gray-500">
            Link wird per E-Mail an <strong>{email}</strong> versendet.
          </p>

          {!tokenResult && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Link-Gültigkeit (Stunden)</label>
                <SelectRoot value={String(ttlHours)} onValueChange={(v) => setTtlHours(Number(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="72">72 Stunden (3 Tage)</SelectItem>
                    <SelectItem value="168">168 Stunden (1 Woche)</SelectItem>
                    <SelectItem value="336">336 Stunden (2 Wochen)</SelectItem>
                    <SelectItem value="720">720 Stunden (30 Tage)</SelectItem>
                    <SelectItem value="2160">2160 Stunden (90 Tage)</SelectItem>
                  </SelectContent>
                </SelectRoot>
              </div>

              {tokenError && (
                <ErrorState
                  title="Link konnte nicht generiert werden"
                  description={tokenError}
                />
              )}

              <Button
                variant="primary"
                disabled={tokenLoading}
                onClick={sendPhase1Token}
                className="w-full"
              >
                {tokenLoading ? <Spinner size="sm" className="mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                Phase-1-Link generieren &amp; E-Mail versenden
              </Button>
            </>
          )}

          {tokenResult && (
            <div className="space-y-4">
              <div className="flex flex-col items-center gap-3 text-center">
                <div className="rounded-full bg-green-100 p-4">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <p className="font-medium text-gray-800">
                  {tokenResult.emailSent
                    ? 'Phase-1-Link generiert und E-Mail versendet!'
                    : 'Phase-1-Link generiert (E-Mail-Versand fehlgeschlagen – bitte manuell senden)'}
                </p>
                {!tokenResult.emailSent && (
                  <p className="text-xs text-amber-600">E-Mail konnte nicht automatisch versendet werden. Kopiere den Link unten und sende ihn manuell.</p>
                )}
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center gap-2">
                <span className="text-xs text-gray-600 font-mono break-all flex-1 min-w-0">{tokenResult.portalUrl}</span>
                <Button
                  variant="ghost"
                  size="xs"
                  aria-label="Link kopieren"
                  onClick={() => {
                    navigator.clipboard.writeText(tokenResult.portalUrl)
                    toast({ title: 'Link kopiert' })
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>

              <p className="text-xs text-gray-400 text-center">
                Gültig bis: {new Date(tokenResult.expiresAt).toLocaleDateString('de-DE')}
              </p>

              <p className="text-xs text-gray-500 text-center bg-gray-50 rounded p-2">
                Nach Bestätigung durch den Kunden können Sie im Onboarding-Detail den Phase-2-Link (Vertragsdokumente) versenden.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── Footer navigation ────────────────────────────────────────────────── */}
      <div className="flex justify-between mt-6 pt-4 border-t border-gray-100">
        <div>
          {step > 1 && step < 4 && (
            <Button variant="ghost" onClick={back} disabled={step === 3 && sapStatus === 'loading'}>
              Zurück
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          {step < 4 && (
            <>
              <Button variant="ghost" onClick={onClose}>
                Abbrechen
              </Button>
              <Button
                variant="primary"
                onClick={next}
                disabled={
                  (step === 1 && !step1Valid) ||
                  (step === 2 && !step2Valid) ||
                  (step === 3 && (sapStatus === 'loading' || (!requestId && sapStatus === 'error')))
                }
              >
                Weiter
              </Button>
            </>
          )}
          {step === 4 && tokenResult && (
            <Button variant="primary" onClick={onComplete}>
              Abschließen
            </Button>
          )}
          {step === 4 && !tokenResult && (
            <Button variant="ghost" onClick={onClose}>
              Abbrechen
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}
